// Fixed square cells (100x100) Schedule-X Calendar Component

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Segment, Header, Button, Message } from "semantic-ui-react";
import { ScheduleXCalendar, useCalendarApp } from "@schedule-x/react";
import { createViewMonthGrid } from "@schedule-x/calendar";
import { createEventsServicePlugin } from "@schedule-x/events-service";
import "@schedule-x/theme-default/dist/index.css";
import { sessionManager } from "@authentication/services/SessionManager";

const darkenColor = (hex, amt) => {
  const num = parseInt(hex.slice(1), 16);
  const r = Math.max(0, (num >> 16) - Math.round(255 * amt));
  const g = Math.max(0, ((num >> 8) & 0xff) - Math.round(255 * amt));
  const b = Math.max(0, (num & 0xff) - Math.round(255 * amt));
  return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
};

const getTextColor = (bg) => {
  if (!bg) return "#000";
  const rgb = parseInt(bg.substring(1), 16);
  const brightness =
    (0.299 * (rgb >> 16) + 0.587 * ((rgb >> 8) & 0xff) + 0.114 * (rgb & 0xff)) /
    255;
  return brightness > 0.5 ? "#000" : "#fff";
};

const CalendarComponent = ({
  events = [],
  canEdit = false,
  onEventClick,
  onAddEvent,
  title = "ACADEMIC CALENDAR",
  subtitle = "A.Y. 2025–2026",
  showManagement = true,
}) => {
  const eventsPlugin = useRef(createEventsServicePlugin());
  const [isAllowedToEdit, setIsAllowedToEdit] = useState(false);

  useEffect(() => {
    const user = sessionManager.user;
    if (user && user.roles) {
      const roles = Array.isArray(user.roles)
        ? user.roles.map((r) => r.toLowerCase())
        : [user.roles.toLowerCase()];
      setIsAllowedToEdit(
        roles.includes("admin") || roles.includes("librarian")
      );
    }
  }, []);

  const calendar = useCalendarApp({
    views: [createViewMonthGrid()],
    defaultView: "monthGrid",
    events,
    plugins: [eventsPlugin.current],
    callbacks: {
      onEventClick: (event) => {
        if (onEventClick) {
          onEventClick(event);
        }
      },
    },
  });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    handleResize(); // initialize on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (calendar && eventsPlugin.current) {
      eventsPlugin.current.set(events);
    }
  }, [events, calendar]);

  useEffect(() => {
    const applyColors = () => {
      document.querySelectorAll("[data-event-id]").forEach((el) => {
        const event = events.find(
          (e) => e.id === el.getAttribute("data-event-id")
        );
        if (event?.color) {
          el.style.backgroundColor = event.color;
          el.style.borderColor = darkenColor(event.color, 0.15);
          el.style.color = getTextColor(event.color);
        }
      });
    };
    const interval = setInterval(applyColors, 500);
    return () => clearInterval(interval);
  }, [events]);

  // Inject fixed square cell styles into the document
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .sx__month-grid-day {

        min-height: 100px !important;
        max-height: 100px !important;
        box-sizing: border-box;
      }
      .sx__month-grid {
        grid-template-columns: repeat(7, 100px) !important;
        justify-content: center !important;
      }
        @media (max-width: 767px) {
        .sx__month-grid {
          grid-template-columns: repeat(7, 50px) !important;
        }
        .sx__month-grid-day{
          max-height: 50px !important;

        }

      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Segment
      raised
      style={{
        background: "#ffffff",
        borderRadius: "8px",
        padding: "1em",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        marginTop: "3em",
        width: "100%",
        maxWidth: "1500px",
        marginLeft: "auto",
        marginRight: "auto",
        display: "flex",
        flexDirection: "column",
        flex: 1,
      }}
    >
      <Header
        as={isMobile ? "h4" : "h2"}
        color="green"
        textAlign="center"
        style={{ marginBottom: "0.5em" }}
      >
        {title}
      </Header>
      {subtitle && (
        <Header
          as={isMobile ? "h6" : "h4"}
          color="orange"
          textAlign="center"
          style={{ marginTop: 0 }}
        >
          {subtitle}
        </Header>
      )}

      {isAllowedToEdit && showManagement && (
        <div style={{ marginBottom: "1em" }}>
          <Message info>
            <Message.Header style={{ fontSize: isMobile ? "small" : "medium" }}>
              Calendar Management
            </Message.Header>
            <p style={{ fontSize: isMobile ? "small" : "medium" }}>
              Total Events: {events.length}{" "}
            </p>
          </Message>
          <Button
            color="blue"
            size="small"
            onClick={onAddEvent}
            disabled={!onAddEvent}
          >
            Add Event
          </Button>
        </div>
      )}

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          overflow: "auto",
          minHeight: "500px",
        }}
      >
        <ScheduleXCalendar
          calendarApp={calendar}
          style={{
            flex: 1,
            width: "max-content",
            height: "max-content",
          }}
        />
      </div>
    </Segment>
  );
};

CalendarComponent.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      start: PropTypes.string.isRequired,
      end: PropTypes.string,
      description: PropTypes.string,
      color: PropTypes.string,
    })
  ),
  canEdit: PropTypes.bool,
  onEventClick: PropTypes.func,
  onAddEvent: PropTypes.func,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  showManagement: PropTypes.bool,
};

CalendarComponent.defaultProps = {
  events: [],
  canEdit: false,
  onEventClick: null,
  onAddEvent: null,
  title: "ACADEMIC CALENDAR",
  subtitle: "A.Y. 2025–2026",
  showManagement: true,
};

export default CalendarComponent;
